import fetchLot from './fetchLot';
import fetchLotsUsingSource from './fetchLotsUsingSource';
import fetchTravelersUsingLot from './fetchTravelersUsingLot';

/**
 * Walk the downstream consumption graph from one lot, breadth-first:
 * per hop, every assembled lot that consumed a frontier lot as a source
 * (lots.sourceLotIds array-contains) and every traveler it was
 * installed on (travelers.componentLotIds array-contains). Already
 * visited lots are skipped, so diamonds and cycles can't loop and the
 * walk is bounded by the actual graph size. Deliberately uncapped: a
 * quarantine recall must cover everything downstream, however big —
 * correctness beats bounding reads.
 *
 * Edge maps are keyed by parent lot ID; a traveler that consumed
 * several lots in the graph appears in each parent's edge list but only
 * once in travelersById.
 *
 * @param {string} lotId - Root lot doc ID.
 * @returns {Promise<{
 *   root: object,
 *   lotsById: Object<string, object>,
 *   travelersById: Object<string, object>,
 *   childLotIdsByLotId: Object<string, string[]>,
 *   travelerIdsByLotId: Object<string, string[]>,
 * }>}
 */
export default async function traceLotDownstream(lotId) {
  const root = await fetchLot(lotId);
  const lotsById = { [root.id]: root };
  const travelersById = {};
  const childLotIdsByLotId = {};
  const travelerIdsByLotId = {};

  let frontier = [root.id];
  while (frontier.length > 0) {
    // Hops are sequential on purpose — each frontier comes from the last.
    // eslint-disable-next-line no-await-in-loop
    const hops = await Promise.all(frontier.map(async (id) => {
      const [childLots, travelers] = await Promise.all([
        fetchLotsUsingSource(id),
        fetchTravelersUsingLot(id),
      ]);
      return { id, childLots, travelers };
    }));

    const nextFrontier = [];
    hops.forEach(({ id, childLots, travelers }) => {
      childLotIdsByLotId[id] = childLots.map((lot) => lot.id);
      travelerIdsByLotId[id] = travelers.map((traveler) => traveler.id);
      travelers.forEach((traveler) => { travelersById[traveler.id] = traveler; });
      childLots.forEach((lot) => {
        if (lotsById[lot.id]) return;
        lotsById[lot.id] = lot;
        nextFrontier.push(lot.id);
      });
    });

    frontier = nextFrontier;
  }

  return {
    root, lotsById, travelersById, childLotIdsByLotId, travelerIdsByLotId,
  };
}
